/*
 * Copyright (c) 2026
 */
import {
  createSnowflakeAtlas,
  PENIS_CRYSTAL_CELL,
  PENIS_SHAPE,
  SNOWFLAKE_ATLAS_COLUMNS,
  SNOWFLAKE_TYPE_COUNT,
} from './snowflake-art'

const VERTEX_SHADER = `
attribute vec2 aClip;
attribute vec2 aUv;
attribute float aOpacity;
varying vec2 vUv;
varying float vOpacity;
void main() {
  vUv = aUv;
  vOpacity = aOpacity;
  gl_Position = vec4(aClip, 0.0, 1.0);
}
`

const FRAGMENT_SHADER = `
precision mediump float;
uniform sampler2D uAtlas;
varying vec2 vUv;
varying float vOpacity;
void main() {
  if (vUv.x < -0.5) {
    if (vOpacity < 0.004) {
      discard;
    }
    gl_FragColor = vec4(vOpacity, vOpacity, vOpacity, vOpacity);
    return;
  }
  vec4 tex = texture2D(uAtlas, vUv);
  float alpha = tex.a * vOpacity;
  if (alpha < 0.004) {
    discard;
  }
  gl_FragColor = vec4(tex.rgb * vOpacity, alpha);
}
`

/** Viewport width, in CSS pixels, where the flake count switches to the larger set. */
const MD_WIDTH = 768
/** How many flakes to simulate below MD_WIDTH. */
const FLAKES_BASE = 25
/** How many flakes to simulate at MD_WIDTH and above. */
const FLAKES_MD = 40
/** Vertex-buffer capacity. Must be at least the larger flake count. */
const MAX_FLAKES = FLAKES_MD
/** Floats stored per vertex: clip x, clip y, atlas u, atlas v, opacity. */
const FLOATS_PER_VERTEX = 5
/** Vertices in one flake quad (two triangles). */
const VERTICES_PER_FLAKE = 6
/** Longest frame step, in seconds. Stops a hitch from teleporting flakes. */
const MAX_FRAME_SECONDS = 0.05
/** Extra horizontal room, as a fraction of the viewport, before a flake wraps. */
const WRAP_MARGIN = 0.08
/** Viewport height where a flake fading in from the top reaches full opacity. */
const FADE_IN_END = 0.05
/** Viewport height where a flake starts fading out toward the bottom. */
const FADE_OUT_START = 0.96
/** Strongest gust, in viewport widths per second, before depth scaling. */
const MAX_GUST = 0.045
/** Strength of the slow side-to-side breeze, in viewport widths per second. */
const PREVAILING_AMPLITUDE = 0.012
/** How fast the prevailing breeze oscillates. Higher is a quicker sway. */
const PREVAILING_SPEED = 0.05
/** How quickly the wind eases toward a new gust. Higher snaps over faster. */
const WIND_RESPONSE = 0.7
/** Shortest time, in seconds, before a new gust target is chosen. */
const GUST_MIN_SECONDS = 4
/** Longest time, in seconds, before a new gust target is chosen. */
const GUST_MAX_SECONDS = 9
/** How much wind speeds up a flake's spin. 0 keeps the spin independent of wind. */
const SPIN_WIND = 8
/** Atlas column of the penis snowflake. */
const PENIS_TYPE = 1
/** Atlas column of the vulva snowflake. */
const VULVA_TYPE = 2
/** Gap between a penis and a vulva, in CSS pixels, that starts the erection. */
const APPROACH_PX = 30
/** Gap, in CSS pixels, that cancels the erection so a passing flake can finish. */
const RELEASE_PX = 80
/** Seconds for the shaft to grow from resting length to twice that length. */
const GROW_SECONDS = 0.7
/** Seconds for a fully grown penis to turn until it points at the vulva. */
const AIM_SECONDS = 0.45
/** Seconds between ejaculations once the penis is aimed. */
const DROP_INTERVAL = 3
/** Semen speed toward the vulva, in CSS pixels per second. */
const DROP_SPEED = 150
/** Fraction of erect length the shaft loses at the bottom of each pulse. */
const PULSE_SHRINK = 0.15
/** Seconds for one ejaculation pulse: a quick shrink, then a grow back to full length. */
const PULSE_SECONDS = 0.14
/** Most semen drops kept alive at once. Older drops are dropped after this. */
const MAX_DROPS = 10
/** Segments around each erect outline ring. Higher is smoother and costs more vertices. */
const ELLIPSE_SEGMENTS = 10
/** Vertices used by one outline ring. */
const ELLIPSE_RING_VERTS = ELLIPSE_SEGMENTS * 6
/** Vertices used by the two sides of the erect shaft outline. */
const SHAFT_OUTLINE_VERTS = 12
/** Vertices for one erect penis: shaft sides plus both balls and the glans. */
const ERECTION_VERTS = SHAFT_OUTLINE_VERTS + ELLIPSE_RING_VERTS * 3
/** Total vertex budget for flakes, erections, and semen. */
const MAX_VERTICES = MAX_FLAKES * VERTICES_PER_FLAKE + MAX_FLAKES * ERECTION_VERTS + MAX_DROPS * VERTICES_PER_FLAKE
/** Smallest flake, in CSS pixels. Also the near end of the depth scale. */
const SIZE_MIN = 20
/** Largest flake, in CSS pixels. Also the far end of the depth scale. */
const SIZE_MAX = 40
/** Size groups. `chance` is how often the group is picked; `min` and `max` are CSS pixels. */
const SIZE_BANDS = [
  { chance: 0.5, min: 20, max: 24 },
  { chance: 0.32, min: 26, max: 30 },
  { chance: 0.18, min: 33, max: 40 },
] as const

function pickFlakeSize(): number {
  const roll = Math.random()
  let cursor = 0
  for (const band of SIZE_BANDS) {
    cursor += band.chance
    if (roll < cursor) {
      return band.min + Math.random() * (band.max - band.min)
    }
  }
  const largest = SIZE_BANDS[SIZE_BANDS.length - 1]
  return largest.min + Math.random() * (largest.max - largest.min)
}

/** Local corner x, local corner y (+y down), cell u, cell v (v = 1 at the top). */
const QUAD = [
  [-1, -1, 0, 1],
  [1, -1, 1, 1],
  [1, 1, 1, 0],
  [-1, -1, 0, 1],
  [1, 1, 1, 0],
  [-1, 1, 0, 0],
] as const

/** WebGL context flags. Low power, transparent canvas, no depth or stencil. */
const GL_ATTRIBUTES: WebGLContextAttributes = {
  alpha: true,
  antialias: false,
  depth: false,
  stencil: false,
  premultipliedAlpha: true,
  preserveDrawingBuffer: false,
  powerPreference: 'low-power',
}

type Flake = {
  x: number
  y: number
  depth: number
  size: number
  opacity: number
  fallSpeed: number
  spin: number
  spinSpeed: number
  type: number
  wobblePhase: number
  wobbleFreq: number
  wobbleAmp: number
  /** Index of the vulva this penis is responding to, or -1. */
  mate: number
  /** 0 = resting shaft, 1 = twice as long. */
  growth: number
  /** 0 = still turning, 1 = aimed at the mate. */
  aim: number
  aimFrom: number
  emit: number
  /** Seconds since the last ejaculation. Drives the shaft pulse. */
  pulse: number
}

type Drop = {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  size: number
  mate: number
}

type Point = {
  x: number
  y: number
}

type WindState = {
  current: number
  target: number
  gustIn: number
}

function flakeCount(cssWidth: number): number {
  return cssWidth >= MD_WIDTH ? FLAKES_MD : FLAKES_BASE
}

function createFlake(scattered: boolean): Flake {
  const size = pickFlakeSize()
  // Larger flakes sit closer: they fall faster and take more of the wind.
  const depth = (size - SIZE_MIN) / (SIZE_MAX - SIZE_MIN)
  const duration = 11 + Math.random() * 16
  return {
    x: Math.random(),
    y: scattered ? -0.18 + Math.random() * 1.2 : 0.08 + Math.random() * 0.84,
    depth,
    size,
    opacity: 0.42 + depth * 0.53,
    fallSpeed: (1 / duration) * (0.5 + depth * 0.75),
    spin: Math.random() * Math.PI * 2,
    spinSpeed: (Math.random() - 0.5) * 1.1,
    type: Math.floor(Math.random() * SNOWFLAKE_TYPE_COUNT),
    wobblePhase: Math.random() * Math.PI * 2,
    wobbleFreq: 0.7 + Math.random() * 0.9,
    wobbleAmp: size * 0.2,
    mate: -1,
    growth: 0,
    aim: 0,
    aimFrom: 0,
    emit: 0,
    pulse: PULSE_SECONDS,
  }
}

function syncFlakes(flakes: Flake[], count: number, scattered: boolean) {
  while (flakes.length < count) {
    flakes.push(createFlake(scattered))
  }
  if (flakes.length > count) {
    flakes.length = count
  }
}

/**
 * Gusts ease toward a new target, and a slow prevailing breeze is added on top.
 * The returned speed is in viewport widths per second.
 */
function stepWind(wind: WindState, dt: number, elapsed: number): number {
  wind.gustIn -= dt
  if (wind.gustIn <= 0) {
    wind.target = (Math.random() * 2 - 1) * MAX_GUST
    wind.gustIn = GUST_MIN_SECONDS + Math.random() * (GUST_MAX_SECONDS - GUST_MIN_SECONDS)
  }
  const blend = 1 - Math.exp(-WIND_RESPONSE * dt)
  wind.current += (wind.target - wind.current) * blend
  return wind.current + Math.sin(elapsed * PREVAILING_SPEED) * PREVAILING_AMPLITUDE
}

function wrapX(x: number): number {
  const span = 1 + WRAP_MARGIN * 2
  if (x > 1 + WRAP_MARGIN) {
    return x - span
  }
  if (x < -WRAP_MARGIN) {
    return x + span
  }
  return x
}

/**
 * Wind is integrated on the CPU. A `time * speed` shader term rewinds flakes
 * when a gust changes direction.
 */
function stepFlakes(flakes: Flake[], dt: number, elapsed: number, windSpeed: number) {
  for (const flake of flakes) {
    const flutter = 1 + Math.sin(elapsed * flake.wobbleFreq * 0.5 + flake.wobblePhase) * 0.06
    flake.x = wrapX(flake.x + windSpeed * flake.depth * dt)
    flake.y += flake.fallSpeed * flutter * dt
    if (flake.mate < 0) {
      flake.spin += flake.spinSpeed * (1 + Math.abs(windSpeed) * SPIN_WIND) * dt
    }
    if (flake.y > 1.04) {
      flake.y = -0.16 - Math.random() * 0.22
      flake.x = wrapX(flake.x + (Math.random() - 0.5) * 0.05)
      flake.mate = -1
      flake.growth = 0
      flake.aim = 0
      flake.pulse = PULSE_SECONDS
    }
  }
}

function flakeOrigin(flake: Flake, cssWidth: number, cssHeight: number, elapsed: number): Point {
  const wobble = Math.sin(elapsed * flake.wobbleFreq + flake.wobblePhase) * flake.wobbleAmp
  const bob = Math.cos(elapsed * flake.wobbleFreq + flake.wobblePhase) * flake.wobbleAmp * 0.35
  return {
    x: flake.x * cssWidth + wobble,
    y: flake.y * cssHeight + bob,
  }
}

function edgeGap(penis: Flake, vulva: Flake, penisAt: Point, vulvaAt: Point): number {
  return Math.hypot(penisAt.x - vulvaAt.x, penisAt.y - vulvaAt.y) - penis.size * 0.5 - vulva.size * 0.5
}

function nearestVulva(flakes: Flake[], places: Point[], penisIndex: number): number {
  const penis = flakes[penisIndex]
  const penisAt = places[penisIndex]
  if (!(penis && penisAt)) {
    return -1
  }
  let nearest = -1
  let nearestGap = APPROACH_PX
  for (let index = 0; index < flakes.length; index++) {
    const vulva = flakes[index]
    const vulvaAt = places[index]
    if (!(vulva && vulvaAt) || vulva.type !== VULVA_TYPE) {
      continue
    }
    const gap = edgeGap(penis, vulva, penisAt, vulvaAt)
    if (gap <= nearestGap) {
      nearest = index
      nearestGap = gap
    }
  }
  return nearest
}

/** Spin that aims the glans (+ local -y) at a screen point. */
function aimSpin(from: Point, to: Point): number {
  return Math.atan2(to.x - from.x, -(to.y - from.y))
}

function lerpAngle(from: number, to: number, t: number): number {
  let delta = to - from
  while (delta > Math.PI) {
    delta -= Math.PI * 2
  }
  while (delta < -Math.PI) {
    delta += Math.PI * 2
  }
  return from + delta * t
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t)
}

type PenisPose = {
  dirX: number
  dirY: number
  perpX: number
  perpY: number
  rootX: number
  rootY: number
  endX: number
  endY: number
  glansX: number
  glansY: number
  tipX: number
  tipY: number
  shaftHalf: number
  outline: number
  glansRx: number
  glansRy: number
  ballRadius: number
  leftBallX: number
  leftBallY: number
  rightBallX: number
  rightBallY: number
}

function penisPose(flake: Flake, origin: Point): PenisPose {
  const cos = Math.cos(flake.spin)
  const sin = Math.sin(flake.spin)
  const half = flake.size * 0.5
  const dirX = sin
  const dirY = -cos
  const local = (lx: number, ly: number) => {
    const px = lx * half
    const py = ly * half
    return {
      x: origin.x + px * cos - py * sin,
      y: origin.y + px * sin + py * cos,
    }
  }
  const root = local(0, PENIS_SHAPE.rootY)
  const length = PENIS_SHAPE.shaftLength * (1 + flake.growth) * pulseScale(flake.pulse) * half
  const endX = root.x + dirX * length
  const endY = root.y + dirY * length
  const glansDistance = length + PENIS_SHAPE.glansOffset * half
  const glansX = root.x + dirX * glansDistance
  const glansY = root.y + dirY * glansDistance
  const glansRy = PENIS_SHAPE.glansRy * half
  const leftBall = local(-PENIS_SHAPE.ballX, PENIS_SHAPE.ballY)
  const rightBall = local(PENIS_SHAPE.ballX, PENIS_SHAPE.ballY)
  return {
    dirX,
    dirY,
    perpX: cos,
    perpY: sin,
    rootX: root.x,
    rootY: root.y,
    endX,
    endY,
    glansX,
    glansY,
    tipX: glansX + dirX * glansRy,
    tipY: glansY + dirY * glansRy,
    shaftHalf: PENIS_SHAPE.shaftHalfWidth * half,
    outline: Math.max(1.15, PENIS_SHAPE.outline * half),
    glansRx: PENIS_SHAPE.glansRx * half,
    glansRy,
    ballRadius: PENIS_SHAPE.ballRadius * half,
    leftBallX: leftBall.x,
    leftBallY: leftBall.y,
    rightBallX: rightBall.x,
    rightBallY: rightBall.y,
  }
}

/** 1 at full length. Dips just after an ejaculation, then eases back. */
function pulseScale(pulse: number): number {
  if (pulse >= PULSE_SECONDS) {
    return 1
  }
  const t = pulse / PULSE_SECONDS
  const attack = 0.25
  const dip = t < attack ? t / attack : 1 - (t - attack) / (1 - attack)
  return 1 - PULSE_SHRINK * dip
}

function advancePulse(flake: Flake, dt: number) {
  if (flake.pulse < PULSE_SECONDS) {
    flake.pulse = Math.min(PULSE_SECONDS, flake.pulse + dt)
  }
}

function releaseMate(flake: Flake, dt: number) {
  advancePulse(flake, dt)
  flake.growth = Math.max(0, flake.growth - dt / GROW_SECONDS)
  flake.emit = 0
  if (flake.growth === 0) {
    flake.mate = -1
    flake.aim = 0
    flake.pulse = PULSE_SECONDS
  }
}

function engageMate(flake: Flake, place: Point, mateAt: Point, dt: number, drops: Drop[]) {
  if (flake.growth < 1) {
    flake.growth = Math.min(1, flake.growth + dt / GROW_SECONDS)
    flake.aim = 0
    flake.aimFrom = flake.spin
    return
  }
  const target = aimSpin(place, mateAt)
  if (flake.aim < 1) {
    flake.aim = Math.min(1, flake.aim + dt / AIM_SECONDS)
    flake.spin = lerpAngle(flake.aimFrom, target, smoothstep(flake.aim))
    return
  }
  flake.spin = target
  advancePulse(flake, dt)
  flake.emit -= dt
  if (flake.emit > 0) {
    return
  }
  spawnDrops(drops, penisPose(flake, place), flake.mate)
  flake.emit = DROP_INTERVAL
  flake.pulse = 0
}

function spawnDrops(drops: Drop[], pose: PenisPose, mate: number) {
  for (let shot = 0; shot < 2; shot++) {
    if (drops.length >= MAX_DROPS) {
      drops.shift()
    }
    const spread = (Math.random() - 0.5) * 0.6
    const angle = Math.atan2(pose.dirY, pose.dirX) + spread
    drops.push({
      x: pose.tipX,
      y: pose.tipY,
      vx: Math.cos(angle) * DROP_SPEED,
      vy: Math.sin(angle) * DROP_SPEED,
      life: 0.55,
      size: 3.2 + Math.random() * 2.4,
      mate,
    })
  }
}

/**
 * A penis within 20px of a vulva grows its shaft, then turns and ejaculates toward it.
 */
function stepMating(flakes: Flake[], places: Point[], dt: number, drops: Drop[]) {
  for (let index = 0; index < flakes.length; index++) {
    const flake = flakes[index]
    const place = places[index]
    if (!(flake && place) || flake.type !== PENIS_TYPE) {
      continue
    }
    if (flake.mate < 0) {
      flake.mate = nearestVulva(flakes, places, index)
      flake.aimFrom = flake.spin
    }
    const mate = flakes[flake.mate]
    const mateAt = places[flake.mate]
    const gap = mate && mateAt && mate.type === VULVA_TYPE ? edgeGap(flake, mate, place, mateAt) : RELEASE_PX + 1
    if (!(mate && mateAt && mate.type === VULVA_TYPE) || gap > RELEASE_PX) {
      releaseMate(flake, dt)
      continue
    }
    engageMate(flake, place, mateAt, dt, drops)
  }
}

function stepDrops(drops: Drop[], flakes: Flake[], places: Point[], dt: number) {
  for (let index = drops.length - 1; index >= 0; index--) {
    const drop = drops[index]
    if (!drop) {
      continue
    }
    const mate = flakes[drop.mate]
    const mateAt = places[drop.mate]
    if (mate && mateAt && mate.type === VULVA_TYPE) {
      const dx = mateAt.x - drop.x
      const dy = mateAt.y - drop.y
      const distance = Math.hypot(dx, dy)
      if (distance < 8) {
        drops.splice(index, 1)
        continue
      }
      drop.vx = (dx / distance) * DROP_SPEED
      drop.vy = (dy / distance) * DROP_SPEED
    }
    drop.x += drop.vx * dt
    drop.y += drop.vy * dt
    drop.life -= dt
    if (drop.life <= 0) {
      drops.splice(index, 1)
    }
  }
}

function edgeFade(y: number): number {
  if (y < FADE_IN_END) {
    return Math.max(0, y / FADE_IN_END)
  }
  if (y > FADE_OUT_START) {
    return Math.max(0, (1 - y) / (1 - FADE_OUT_START))
  }
  return 1
}

function writeVertex(
  data: Float32Array,
  vertex: number,
  x: number,
  y: number,
  cssWidth: number,
  cssHeight: number,
  u: number,
  v: number,
  opacity: number
): number {
  const offset = vertex * FLOATS_PER_VERTEX
  data[offset] = (x / cssWidth) * 2 - 1
  data[offset + 1] = 1 - (y / cssHeight) * 2
  data[offset + 2] = u
  data[offset + 3] = v
  data[offset + 4] = opacity
  return vertex + 1
}

function writeQuad(
  data: Float32Array,
  vertex: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
  cssWidth: number,
  cssHeight: number,
  opacity: number
): number {
  let cursor = vertex
  cursor = writeVertex(data, cursor, ax, ay, cssWidth, cssHeight, -1, 0, opacity)
  cursor = writeVertex(data, cursor, bx, by, cssWidth, cssHeight, -1, 0, opacity)
  cursor = writeVertex(data, cursor, cx, cy, cssWidth, cssHeight, -1, 0, opacity)
  cursor = writeVertex(data, cursor, ax, ay, cssWidth, cssHeight, -1, 0, opacity)
  cursor = writeVertex(data, cursor, cx, cy, cssWidth, cssHeight, -1, 0, opacity)
  cursor = writeVertex(data, cursor, dx, dy, cssWidth, cssHeight, -1, 0, opacity)
  return cursor
}

/** Hollow ellipse. The band sits just outside the shape, matching the symbol outline. */
function writeEllipseRing(
  data: Float32Array,
  vertex: number,
  cx: number,
  cy: number,
  dirX: number,
  dirY: number,
  perpX: number,
  perpY: number,
  rx: number,
  ry: number,
  stroke: number,
  cssWidth: number,
  cssHeight: number,
  opacity: number
): number {
  if (vertex + ELLIPSE_RING_VERTS > MAX_VERTICES) {
    return vertex
  }
  let cursor = vertex
  const ringAt = (angle: number, radiusX: number, radiusY: number) => ({
    x: cx + perpX * Math.cos(angle) * radiusX + dirX * Math.sin(angle) * radiusY,
    y: cy + perpY * Math.cos(angle) * radiusX + dirY * Math.sin(angle) * radiusY,
  })
  for (let segment = 0; segment < ELLIPSE_SEGMENTS; segment++) {
    const start = (segment / ELLIPSE_SEGMENTS) * Math.PI * 2
    const end = ((segment + 1) / ELLIPSE_SEGMENTS) * Math.PI * 2
    const outerStart = ringAt(start, rx + stroke, ry + stroke)
    const outerEnd = ringAt(end, rx + stroke, ry + stroke)
    const innerStart = ringAt(start, rx, ry)
    const innerEnd = ringAt(end, rx, ry)
    cursor = writeQuad(
      data,
      cursor,
      outerStart.x,
      outerStart.y,
      innerStart.x,
      innerStart.y,
      innerEnd.x,
      innerEnd.y,
      outerEnd.x,
      outerEnd.y,
      cssWidth,
      cssHeight,
      opacity
    )
  }
  return cursor
}

function writeFlakes(
  flakes: Flake[],
  places: Point[],
  data: Float32Array,
  cssWidth: number,
  cssHeight: number
): number {
  let vertex = 0
  for (let index = 0; index < flakes.length; index++) {
    const flake = flakes[index]
    const place = places[index]
    if (!(flake && place)) {
      continue
    }
    const opacity = flake.opacity * edgeFade(flake.y)
    if (opacity < 0.01) {
      continue
    }
    const half = flake.size * 0.5
    const cos = Math.cos(flake.spin)
    const sin = Math.sin(flake.spin)
    const cell = flake.type === PENIS_TYPE && flake.growth > 0 ? PENIS_CRYSTAL_CELL : flake.type
    const uBase = cell / SNOWFLAKE_ATLAS_COLUMNS
    for (let corner = 0; corner < VERTICES_PER_FLAKE; corner++) {
      const [lx, ly, cellU, cellV] = QUAD[corner]
      const px = lx * half
      const py = ly * half
      const screenX = place.x + px * cos - py * sin
      const screenY = place.y + px * sin + py * cos
      vertex = writeVertex(
        data,
        vertex,
        screenX,
        screenY,
        cssWidth,
        cssHeight,
        uBase + cellU / SNOWFLAKE_ATLAS_COLUMNS,
        cellV,
        opacity
      )
    }
  }
  return vertex
}

function writeErections(
  flakes: Flake[],
  places: Point[],
  data: Float32Array,
  vertex: number,
  cssWidth: number,
  cssHeight: number
): number {
  let cursor = vertex
  for (let index = 0; index < flakes.length; index++) {
    const flake = flakes[index]
    const place = places[index]
    if (!(flake && place) || flake.type !== PENIS_TYPE || flake.growth <= 0) {
      continue
    }
    const opacity = flake.opacity * edgeFade(flake.y)
    if (opacity < 0.01 || cursor + ERECTION_VERTS > MAX_VERTICES) {
      continue
    }
    const pose = penisPose(flake, place)
    cursor = writeEllipseRing(
      data,
      cursor,
      pose.leftBallX,
      pose.leftBallY,
      pose.dirX,
      pose.dirY,
      pose.perpX,
      pose.perpY,
      pose.ballRadius,
      pose.ballRadius,
      pose.outline,
      cssWidth,
      cssHeight,
      opacity
    )
    cursor = writeEllipseRing(
      data,
      cursor,
      pose.rightBallX,
      pose.rightBallY,
      pose.dirX,
      pose.dirY,
      pose.perpX,
      pose.perpY,
      pose.ballRadius,
      pose.ballRadius,
      pose.outline,
      cssWidth,
      cssHeight,
      opacity
    )
    for (const side of [1, -1]) {
      const innerX = pose.perpX * side * pose.shaftHalf
      const innerY = pose.perpY * side * pose.shaftHalf
      const outerX = pose.perpX * side * (pose.shaftHalf + pose.outline)
      const outerY = pose.perpY * side * (pose.shaftHalf + pose.outline)
      cursor = writeQuad(
        data,
        cursor,
        pose.rootX + innerX,
        pose.rootY + innerY,
        pose.rootX + outerX,
        pose.rootY + outerY,
        pose.endX + outerX,
        pose.endY + outerY,
        pose.endX + innerX,
        pose.endY + innerY,
        cssWidth,
        cssHeight,
        opacity
      )
    }
    cursor = writeEllipseRing(
      data,
      cursor,
      pose.glansX,
      pose.glansY,
      pose.dirX,
      pose.dirY,
      pose.perpX,
      pose.perpY,
      pose.glansRx,
      pose.glansRy,
      pose.outline,
      cssWidth,
      cssHeight,
      opacity
    )
  }
  return cursor
}

function writeDrops(drops: Drop[], data: Float32Array, vertex: number, cssWidth: number, cssHeight: number): number {
  let cursor = vertex
  for (const drop of drops) {
    if (cursor + VERTICES_PER_FLAKE > MAX_VERTICES) {
      break
    }
    const opacity = Math.max(0, drop.life / 0.55) * 0.95
    const half = drop.size * 0.5
    cursor = writeVertex(data, cursor, drop.x - half, drop.y - half, cssWidth, cssHeight, -1, 0, opacity)
    cursor = writeVertex(data, cursor, drop.x + half, drop.y - half, cssWidth, cssHeight, -1, 0, opacity)
    cursor = writeVertex(data, cursor, drop.x + half, drop.y + half, cssWidth, cssHeight, -1, 0, opacity)
    cursor = writeVertex(data, cursor, drop.x - half, drop.y - half, cssWidth, cssHeight, -1, 0, opacity)
    cursor = writeVertex(data, cursor, drop.x + half, drop.y + half, cssWidth, cssHeight, -1, 0, opacity)
    cursor = writeVertex(data, cursor, drop.x - half, drop.y + half, cssWidth, cssHeight, -1, 0, opacity)
  }
  return cursor
}

function compileShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type)
  if (!shader) {
    return null
  }
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader)
    return null
  }
  return shader
}

function backingSize(cssWidth: number, cssHeight: number): { width: number; height: number } {
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  return {
    width: Math.max(1, Math.round(cssWidth * dpr)),
    height: Math.max(1, Math.round(cssHeight * dpr)),
  }
}

/**
 * Draws wind-driven snowflakes into `canvas`. Returns a cleanup function.
 * No-ops when WebGL is unavailable.
 */
export function attachSnowEffect(canvas: HTMLCanvasElement): () => void {
  const gl = canvas.getContext('webgl', GL_ATTRIBUTES)
  if (!gl) {
    return () => undefined
  }

  const atlas = createSnowflakeAtlas()
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER)
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER)
  const program = gl.createProgram()
  if (!(atlas && vertexShader && fragmentShader && program)) {
    if (vertexShader) {
      gl.deleteShader(vertexShader)
    }
    if (fragmentShader) {
      gl.deleteShader(fragmentShader)
    }
    if (program) {
      gl.deleteProgram(program)
    }
    return () => undefined
  }

  gl.attachShader(program, vertexShader)
  gl.attachShader(program, fragmentShader)
  gl.bindAttribLocation(program, 0, 'aClip')
  gl.bindAttribLocation(program, 1, 'aUv')
  gl.bindAttribLocation(program, 2, 'aOpacity')
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program)
    gl.deleteShader(vertexShader)
    gl.deleteShader(fragmentShader)
    return () => undefined
  }

  const buffer = gl.createBuffer()
  const texture = gl.createTexture()
  if (!(buffer && texture)) {
    if (buffer) {
      gl.deleteBuffer(buffer)
    }
    if (texture) {
      gl.deleteTexture(texture)
    }
    gl.deleteProgram(program)
    gl.deleteShader(vertexShader)
    gl.deleteShader(fragmentShader)
    return () => undefined
  }

  const bindProgram = gl.useProgram.bind(gl)

  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true)
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, atlas)

  const vertices = new Float32Array(MAX_VERTICES * FLOATS_PER_VERTEX)
  const flakes: Flake[] = []
  const drops: Drop[] = []
  const wind: WindState = {
    current: 0,
    target: (Math.random() * 2 - 1) * MAX_GUST * 0.35,
    gustIn: 1.5 + Math.random() * 2,
  }

  let disposed = false
  let frame = 0
  let elapsed = 0
  let lastNow = performance.now()
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  let reducedMotion = motionQuery.matches

  const bindState = (width: number, height: number) => {
    gl.viewport(0, 0, width, height)
    bindProgram(program)
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.enableVertexAttribArray(0)
    gl.enableVertexAttribArray(1)
    gl.enableVertexAttribArray(2)
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, FLOATS_PER_VERTEX * 4, 0)
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, FLOATS_PER_VERTEX * 4, 8)
    gl.vertexAttribPointer(2, 1, gl.FLOAT, false, FLOATS_PER_VERTEX * 4, 16)
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.enable(gl.BLEND)
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
  }

  const draw = (now: number) => {
    if (disposed || gl.isContextLost()) {
      return
    }
    const cssWidth = canvas.clientWidth
    const cssHeight = canvas.clientHeight
    if (cssWidth === 0 || cssHeight === 0) {
      return
    }

    const next = backingSize(cssWidth, cssHeight)
    const resized = canvas.width !== next.width || canvas.height !== next.height
    if (resized) {
      canvas.width = next.width
      canvas.height = next.height
    }
    bindState(next.width, next.height)

    const delta = Math.min(MAX_FRAME_SECONDS, Math.max(0, (now - lastNow) / 1000))
    lastNow = now
    syncFlakes(flakes, flakeCount(cssWidth), !reducedMotion)
    if (!reducedMotion) {
      elapsed += delta
      const windSpeed = stepWind(wind, delta, elapsed)
      stepFlakes(flakes, delta, elapsed, windSpeed)
    } else if (drops.length > 0) {
      drops.length = 0
    }

    const places = flakes.map((flake) => flakeOrigin(flake, cssWidth, cssHeight, elapsed))
    if (!reducedMotion) {
      stepMating(flakes, places, delta, drops)
      stepDrops(drops, flakes, places, delta)
    }

    let written = writeFlakes(flakes, places, vertices, cssWidth, cssHeight)
    written = writeErections(flakes, places, vertices, written, cssWidth, cssHeight)
    written = writeDrops(drops, vertices, written, cssWidth, cssHeight)
    gl.clearColor(0, 0, 0, 0)
    gl.clear(gl.COLOR_BUFFER_BIT)
    if (written === 0) {
      return
    }
    gl.bufferData(gl.ARRAY_BUFFER, vertices.subarray(0, written * FLOATS_PER_VERTEX), gl.DYNAMIC_DRAW)
    gl.drawArrays(gl.TRIANGLES, 0, written)
  }

  const loop = (now: number) => {
    if (disposed || reducedMotion || document.hidden || gl.isContextLost()) {
      return
    }
    frame = requestAnimationFrame(loop)
    draw(now)
  }

  const start = () => {
    if (disposed || reducedMotion || document.hidden || gl.isContextLost()) {
      return
    }
    cancelAnimationFrame(frame)
    lastNow = performance.now()
    frame = requestAnimationFrame(loop)
  }

  const onVisibility = () => {
    if (document.hidden) {
      cancelAnimationFrame(frame)
      return
    }
    lastNow = performance.now()
    draw(performance.now())
    start()
  }

  const onMotion = () => {
    reducedMotion = motionQuery.matches
    if (reducedMotion) {
      cancelAnimationFrame(frame)
      draw(performance.now())
      return
    }
    start()
  }

  const resizeObserver = new ResizeObserver(() => {
    lastNow = performance.now()
    draw(performance.now())
  })
  resizeObserver.observe(canvas)
  document.addEventListener('visibilitychange', onVisibility)
  motionQuery.addEventListener('change', onMotion)
  draw(performance.now())
  start()

  return () => {
    disposed = true
    cancelAnimationFrame(frame)
    resizeObserver.disconnect()
    document.removeEventListener('visibilitychange', onVisibility)
    motionQuery.removeEventListener('change', onMotion)
    gl.deleteBuffer(buffer)
    gl.deleteTexture(texture)
    gl.deleteProgram(program)
    gl.deleteShader(vertexShader)
    gl.deleteShader(fragmentShader)
  }
}
